import React, { createContext, useCallback, useContext, useState } from "react";
import { 
  getListaWarehouseUserClientReportDailyPaginated,
  WarehouseUserClientReportDailyResponse, 
  WarehouseUserClientReportDailyVM,
  WarehouseUserClientReportDailyPutVM,
  WarehouseUserClientReportDailyApiResponse, 
  getListaWarehouseUserClientReportDaily, 
  getWarehouseUserClientReportDailyById, 
  WarehouseUserClientReportDailyApiCreateResponse,
  createWarehouseUserClientReportDaily, 
  updateWarehouseUserClientReportDaily, 
  deleteWarehouseUserClientReportDaily 
} from "../../services/warehouseUserClientReportDailyService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import Paginacao from "../../types/paginacao";
import { ApiResponse } from "../../services/api";
import WarehouseUserClientReportDaily from "../../types/warehouseUserClientReportDaily";

interface WarehouseUserClientReportDailyContextProps {
  apiResponse: ApiResponse<WarehouseUserClientReportDaily> | null;
  warehouseUserClientReportDaily: WarehouseUserClientReportDailyResponse[] | WarehouseUserClientReportDaily[] | null;
  warehouseUserClientReportDailyById: WarehouseUserClientReportDailyApiResponse | null;
  loading: boolean;
  error: string | null;
  fetchWarehouseUserClientReportDaily: () => void;
  fetchWarehouseUserClientReportDailyPaginated: (paginacao: Paginacao) => void;
  fetchWarehouseUserClientReportDailyById: (id: number) => Promise<WarehouseUserClientReportDailyApiResponse | null>;
  postWarehouseUserClientReportDaily: (cargo: WarehouseUserClientReportDailyVM) => Promise<WarehouseUserClientReportDailyApiCreateResponse | null>;
  putWarehouseUserClientReportDaily: (id: number, cargo: WarehouseUserClientReportDailyPutVM) => Promise<WarehouseUserClientReportDailyApiResponse | null>;
  excluiWarehouseUserClientReportDaily: (id: number) => Promise<boolean>;
}

interface WarehouseUserClientReportDailyProviderProps {
  children: React.ReactNode;
}

const WarehouseUserClientReportDailyContext = createContext<WarehouseUserClientReportDailyContextProps | null>(null);

const WarehouseUserClientReportDailyProvider: React.FC<WarehouseUserClientReportDailyProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<WarehouseUserClientReportDaily> | null>(null);
  const [warehouseUserClientReportDaily, setWarehouseUserClientReportDaily] = useState<WarehouseUserClientReportDailyResponse[] | WarehouseUserClientReportDaily[] | null>(null);
  const [warehouseUserClientReportDailyById, setWarehouseUserClientReportDailyById] = useState<WarehouseUserClientReportDailyApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWarehouseUserClientReportDaily = useCallback(async () => {
    setError(null);
    try {
      setLoading(true);
      const response = await getListaWarehouseUserClientReportDaily();
      setWarehouseUserClientReportDaily(response.values || []);
    } catch (err) {
      setError(getErrorMessage("fetchWarehouseUserClientReportDailyError"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWarehouseUserClientReportDailyPaginated = useCallback(async (paginacao: Paginacao) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getListaWarehouseUserClientReportDailyPaginated(paginacao);
      setApiResponse(response);
      setWarehouseUserClientReportDaily(response.data.values || []);
    } catch (err) {
      setWarehouseUserClientReportDaily([]);
      setError(getErrorMessage("fetchBranchError"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWarehouseUserClientReportDailyById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getWarehouseUserClientReportDailyById(id);
      setWarehouseUserClientReportDailyById(response);
      return response;
    } catch (err) {
      setError(getErrorMessage("fetchWarehouseUserClientReportDailyByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const postWarehouseUserClientReportDaily = useCallback(async (cargo: WarehouseUserClientReportDailyVM) => {
    setError(null);
    try {
      setLoading(true);
      const response = await createWarehouseUserClientReportDaily(cargo);
      fetchWarehouseUserClientReportDaily();
      return response;
    } catch (err) {
      setError(getErrorMessage("createWarehouseUserClientReportDailyError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWarehouseUserClientReportDaily]);

  const putWarehouseUserClientReportDaily = useCallback(async (id: number, cargo: WarehouseUserClientReportDailyPutVM) => {
    setError(null);
    try {
      setLoading(true);
      const response = await updateWarehouseUserClientReportDaily(id, cargo);
      fetchWarehouseUserClientReportDaily();
      return response;
    } catch (err) {
      setError(getErrorMessage("updateWarehouseUserClientReportDailyError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWarehouseUserClientReportDaily]);

  const excluiWarehouseUserClientReportDaily = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      await deleteWarehouseUserClientReportDaily(id);
      fetchWarehouseUserClientReportDaily();
      return true;
    } catch (err) {
      setError(getErrorMessage("deleteWarehouseUserClientReportDailyError"));
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchWarehouseUserClientReportDaily]);

  return (
    <WarehouseUserClientReportDailyContext.Provider 
      value={{ 
        apiResponse,
        fetchWarehouseUserClientReportDailyPaginated,
        warehouseUserClientReportDaily, 
        warehouseUserClientReportDailyById, 
        loading, 
        error, 
        fetchWarehouseUserClientReportDaily, 
        fetchWarehouseUserClientReportDailyById, 
        postWarehouseUserClientReportDaily, 
        putWarehouseUserClientReportDaily, 
        excluiWarehouseUserClientReportDaily 
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </WarehouseUserClientReportDailyContext.Provider>
  );
};

export default WarehouseUserClientReportDailyProvider;

export const useWarehouseUserClientReportDaily = (): WarehouseUserClientReportDailyContextProps => {
  const context = useContext(WarehouseUserClientReportDailyContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useWarehouseUserClientReportDaily?.name,
        providerName: WarehouseUserClientReportDailyProvider?.name,
      })
    );
  }
  return context;
};
